/**
 * The chase crew: the technicians on the service side of the wall.
 *
 * A chase with nobody in it is a corridor of hardware, and the sub-fab
 * already made the point that a room reads as a workplace the moment
 * somebody is working in it. Three stand and one walks: one at the back
 * of ETCH-01 with a rear panel off, working inside it; one at the support
 * rack behind DEP-02, on the phone to the aisle; one at the MCC line-up;
 * and one on rounds along the -z walkway, who stops for the student the
 * way the walker in the aisle does.
 *
 * GOWNED, NOT IN COVERALLS. The chase is entered from the aisle through a
 * door in the bulkhead, without degowning: it is the same air envelope at
 * a looser class, and the people in it are dressed exactly as the people
 * outside it. So these wear people.ts's gown (the shader, the hood, the
 * goggles and mask) - `gownFigure` is shared for that reason - while the
 * sub-fab crew, a floor down and outside the cleanroom, wear coveralls.
 *
 * ON THE CAST, and talkable. They are the cast members with `post:
 * 'chase'` (cast.ts), so they have names in every catalogue, a persona,
 * and a brief like anyone else's - the etch equipment technician speaks
 * for ETCH-01 from its service side, the other three for their post and
 * their trade, with no tool to quote. The conversation machinery is the
 * same as people.ts's: turned to face the student while engaged, talking
 * clip while a reply is spoken, back to their work afterwards.
 *
 * The posts are derived from the layout - the tool's own back, the rack's
 * front, the MCC's doors - through the same constants chase.ts builds
 * from, so they move with the room.
 */

import * as THREE from 'three'
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js'
import { BULKHEAD_Z, FAB, TOOLS, type Tool } from './layout'
import { mainframeBackD, WALK_D } from './chase'
import { clipSpeed, gownFigure, type GownedFigure } from './people'
import { relaxIdlePose } from './relaxIdle'
import { CAST, type Body, type CastMember } from './cast'

export interface ChaseCrewMember extends GownedFigure {
  /** Who this is (cast.ts). */
  id: string
  walker: { dir: 1 | -1; stopped: boolean } | null
  timer: number
  current: string
  engaged: boolean
  talking: boolean
  /** Where they were facing, and what they were doing, before the talk. */
  home: { yaw: number; clip: string }
}

/** The same conversation surface people.ts offers, so FabScene can route to either. */
export interface ChaseCrew {
  group: THREE.Group
  people: ChaseCrewMember[]
  advance(dt: number, eye: THREE.Vector3): void
  colliders(): Array<{ minX: number; maxX: number; minZ: number; maxZ: number }>
  has(id: string): boolean
  engage(id: string): void
  disengage(): void
  setTalking(on: boolean): void
  engaged(): ChaseCrewMember | null
  personOf(object: THREE.Object3D): string | null
  whereIs(id: string): { x: number; z: number; dir: number } | null
}

/** The -z walkway, end to end short of the last bays. */
export const CHASE_WALK = { z: -(BULKHEAD_Z + WALK_D), minX: -44, maxX: 44, speed: 1.0 } as const

const byTag = (tag: string): Tool | undefined => TOOLS.find((t) => t.assetTag === tag)

interface Placement {
  x: number
  z: number
  /** Which way the figure faces, radians about y; the model looks down its local +z. */
  yaw: number
}

/**
 * Where a chase cast member stands, worked out from the room. Null for the
 * walker, who has a lane rather than a spot.
 */
export function chasePlacement(who: CastMember): Placement | null {
  switch (who.station) {
    case 'back': {
      // A stride off the rear face of their own tool, facing it: on the -z
      // side the back looks down -z, so the figure looks up +z, which is
      // the model's local +z, yaw 0.
      const tool = who.toolId ? TOOLS.find((t) => t.id === who.toolId) : undefined
      if (!tool) return null
      const side = Math.sign(tool.position[2])
      return { x: tool.position[0] + 0.55, z: side * (BULKHEAD_Z + mainframeBackD(tool) + 0.85), yaw: side > 0 ? Math.PI : 0 }
    }
    case 'rack': {
      // On the walkway line behind DEP-02, facing its support rack, which
      // stands further out than the walkway.
      const dep = byTag('DEP-02')
      if (!dep) return null
      const side = Math.sign(dep.position[2])
      const mirror = dep.position[2] > 0 ? 1 : -1
      return { x: dep.position[0] - mirror * 0.3 - 0.5, z: side * (BULKHEAD_Z + WALK_D - 0.05), yaw: side > 0 ? 0 : Math.PI }
    }
    case 'mcc': {
      // The MCC line-up on the -z side: chase.ts stands a run of six
      // sections every 15 m from -60, against the outer wall; this is the
      // second section of the run at 15..20, clear of the parts rack CMP-11's
      // bay stands at 17.75..19.05, facing the doors.
      const far = FAB.bayDepth + FAB.aisleHalfWidth
      return { x: 16.3, z: -(far - 0.62 - 0.32 - 0.6), yaw: Math.PI }
    }
    default:
      return null
  }
}

/** Fetch and gown the crew. Resolves to nothing if no model loads. */
export async function loadChaseCrew(urls: Record<Body, string>): Promise<ChaseCrew | null> {
  const loader = new GLTFLoader()
  const loaded: Partial<Record<Body, GLTF>> = {}
  await Promise.all(
    (Object.keys(urls) as Body[]).map(async (body) => {
      try {
        loaded[body] = await loader.loadAsync(urls[body])
      } catch (err) {
        console.warn(`chase crew model ${body} unavailable:`, err)
      }
    }),
  )
  const any = loaded.f ?? loaded.m
  if (!any) return null
  const g = new THREE.Group()
  const people: ChaseCrewMember[] = []

  const make = (who: CastMember): ChaseCrewMember => {
    const fig = gownFigure(loaded[who.body] ?? any, who.body)
    // The pick walks up from whatever mesh the ray hit to this.
    fig.group.userData.personId = who.id
    return { ...fig, id: who.id, walker: null, timer: 0, current: '', engaged: false, talking: false, home: { yaw: 0, clip: who.clip } }
  }
  const play = (p: ChaseCrewMember, name: string, fade = 0.4): void => {
    const next = p.clips[name]
    if (!next || p.current === name) return
    const prev = p.clips[p.current]
    next.reset().setEffectiveTimeScale(clipSpeed(name)).setEffectiveWeight(1).fadeIn(fade).play()
    if (prev) prev.fadeOut(fade)
    p.current = name
  }

  for (const who of CAST) {
    if (who.post !== 'chase') continue
    const p = make(who)
    if (who.station === 'rounds') {
      // The one on rounds, starting mid-walkway.
      p.walker = { dir: 1, stopped: false }
      p.group.position.set((CHASE_WALK.minX + CHASE_WALK.maxX) / 2, 0, CHASE_WALK.z)
      p.group.rotation.y = Math.PI / 2
      p.home.yaw = p.group.rotation.y
      play(p, 'Walk_Loop', 0)
      p.mixer.update(0.4)
    } else {
      const at = chasePlacement(who)
      if (!at) continue
      p.group.position.set(at.x, 0, at.z)
      p.group.rotation.y = at.yaw
      p.home.yaw = at.yaw
      p.timer = Math.random() * 6
      play(p, who.clip, 0)
      p.mixer.update(Math.random() * 2)
    }
    g.add(p.group)
    people.push(p)
  }

  /** Turn toward a yaw, no faster than a person turns. */
  const turnToward = (p: ChaseCrewMember, target: number, dt: number, rate = 2.5): boolean => {
    let d = target - p.group.rotation.y
    d = Math.atan2(Math.sin(d), Math.cos(d))
    p.group.rotation.y += Math.sign(d) * Math.min(Math.abs(d), rate * dt)
    return Math.abs(d) < 0.02
  }
  /** People turning back to their post after a conversation. */
  const returning = new Set<ChaseCrewMember>()
  /** The idle's fists and elbows, brought down - see relaxIdle.ts. */
  const relax = (p: ChaseCrewMember): void => relaxIdlePose(p.group, p.rest, p.clips.Idle_Loop?.getEffectiveWeight() ?? 0)

  const advance = (dt: number, eye: THREE.Vector3): void => {
    for (const p of people) {
      p.timer += dt
      if (p.engaged) {
        const dx = eye.x - p.group.position.x
        const dz = eye.z - p.group.position.z
        turnToward(p, Math.atan2(dx, dz), dt, 3.0)
        play(p, p.talking ? 'Idle_Talking_Loop' : 'Idle_Loop', 0.3)
        p.mixer.update(dt)
        relax(p)
        continue
      }
      if (returning.has(p)) {
        if (turnToward(p, p.home.yaw, dt)) {
          returning.delete(p)
          play(p, p.home.clip, 0.4)
          if (p.walker) p.walker.stopped = false
        }
        p.mixer.update(dt)
        relax(p)
        continue
      }
      if (p.walker) {
        const w = p.walker
        // Stop for a student in the way, a couple of metres ahead - a
        // student in the chase, not one on the other side of the wall.
        const ax = p.group.position.x + w.dir * 1.6
        const dx = eye.x - ax
        const dz = eye.z - CHASE_WALK.z
        const blocked = eye.y > -0.5 && Math.abs(eye.z) > BULKHEAD_Z && dx * dx + dz * dz < 1.8 * 1.8
        if (blocked !== w.stopped) {
          w.stopped = blocked
          play(p, blocked ? 'Idle_Loop' : 'Walk_Loop', 0.3)
        }
        if (!w.stopped) {
          p.group.position.x += w.dir * CHASE_WALK.speed * dt
          if (p.group.position.x > CHASE_WALK.maxX) w.dir = -1
          if (p.group.position.x < CHASE_WALK.minX) w.dir = 1
          turnToward(p, w.dir > 0 ? Math.PI / 2 : -Math.PI / 2, dt)
        }
      } else if (p.timer > 9) {
        p.timer = 0
        const order = ['Interact', 'Idle_Talking_Loop', 'Idle_Loop']
        const i = (order.indexOf(p.current) + 1) % order.length
        play(p, order[i])
      }
      p.mixer.update(dt)
      relax(p)
    }
  }

  const colliders = () =>
    people
      .filter((p) => !p.walker)
      .map((p) => ({
        minX: p.group.position.x - 0.35,
        maxX: p.group.position.x + 0.35,
        minZ: p.group.position.z - 0.35,
        maxZ: p.group.position.z + 0.35,
      }))

  let inTalk: ChaseCrewMember | null = null
  const has = (id: string): boolean => people.some((q) => q.id === id)
  const engage = (id: string): void => {
    const p = people.find((q) => q.id === id)
    if (!p || inTalk === p) return
    disengage()
    inTalk = p
    returning.delete(p)
    p.engaged = true
    p.talking = false
    if (p.walker) p.home.yaw = p.walker.dir > 0 ? Math.PI / 2 : -Math.PI / 2
  }
  const disengage = (): void => {
    const p = inTalk
    if (!p) return
    inTalk = null
    p.engaged = false
    p.talking = false
    returning.add(p)
  }
  const setTalking = (on: boolean): void => {
    if (inTalk) inTalk.talking = on
  }
  const engaged = (): ChaseCrewMember | null => inTalk
  const personOf = (object: THREE.Object3D): string | null => {
    let o: THREE.Object3D | null = object
    while (o && !o.userData.personId) o = o.parent
    return (o?.userData.personId as string | undefined) ?? null
  }
  const whereIs = (id: string): { x: number; z: number; dir: number } | null => {
    const p = people.find((q) => q.id === id)
    if (!p) return null
    return { x: p.group.position.x, z: p.group.position.z, dir: p.walker?.dir ?? 0 }
  }

  return { group: g, people, advance, colliders, has, engage, disengage, setTalking, engaged, personOf, whereIs }
}
